import { useCallback, useEffect, useState } from 'react';
import type { TotpEnrolment, TwoFactorStatus } from '@hub/shared';
import { api } from '../api/client';
import { ActionButton, Alert, Card, Loading, Pill, SectionTitle, fmtDate, useFlash } from './ui';

/** Enrol, disable, and re-issue backup codes for TOTP two-factor auth. */
export function TwoFactorCard({ onChanged }: { onChanged?: () => void }) {
  const [status, setStatus] = useState<TwoFactorStatus | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [enrolment, setEnrolment] = useState<TotpEnrolment | null>(null);
  const [code, setCode] = useState('');
  const [password, setPassword] = useState('');
  // Shown once after enrolling or regenerating; the server only keeps hashes.
  const [backupCodes, setBackupCodes] = useState<string[] | null>(null);
  const flash = useFlash();

  const load = useCallback(async () => {
    try {
      setStatus(await api.get<TwoFactorStatus>('/api/auth/2fa'));
      setError(null);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not read two-factor status');
    }
  }, []);

  useEffect(() => { void load(); }, [load]);

  if (error) return <Card style={{ marginBottom: '1.5rem' }}><Alert tone="error">{error}</Alert></Card>;
  if (!status) return <Card style={{ marginBottom: '1.5rem' }}><Loading /></Card>;

  return (
    <Card style={{ marginBottom: '1.5rem' }}>
      <SectionTitle actions={flash.node}>Two-factor authentication</SectionTitle>

      <div style={{ display: 'flex', alignItems: 'center', gap: '0.6rem', marginBottom: '0.75rem' }}>
        <Pill tone={status.enabled ? 'emerald' : 'muted'}>{status.enabled ? 'enabled' : 'disabled'}</Pill>
        {status.enabled && (
          <span className="muted" style={{ fontSize: '0.8rem' }}>
            since {fmtDate(status.enabledAt)} · {status.backupCodesRemaining} backup code
            {status.backupCodesRemaining === 1 ? '' : 's'} left
          </span>
        )}
      </div>

      {backupCodes && (
        <div className="alert-success" style={{ marginBottom: '1rem', fontSize: '0.85rem' }}>
          <div style={{ marginBottom: '0.5rem' }}>
            <strong>Save these backup codes now.</strong> Each works once if you lose your
            authenticator. They are shown only this once.
          </div>
          <div className="mono" style={{
            display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(9rem, 1fr))',
            gap: '0.2rem 1rem', margin: '0.5rem 0',
          }}>
            {backupCodes.map((c) => <span key={c}>{c}</span>)}
          </div>
          <button
            type="button" className="btn-ghost btn-sm"
            onClick={() => {
              void navigator.clipboard?.writeText(backupCodes.join('\n')).then(() => flash.show('Copied', 'ok'));
            }}
          >Copy all</button>
          <button
            type="button" className="btn-ghost btn-sm" style={{ marginLeft: '0.4rem' }}
            onClick={() => setBackupCodes(null)}
          >Dismiss</button>
        </div>
      )}

      {!status.enabled && !enrolment && (
        <>
          <p className="muted" style={{ fontSize: '0.8rem', marginTop: 0 }}>
            Protect sign-in with a code from an authenticator app (1Password, Authy, Google
            Authenticator, …). API keys keep working unchanged.
          </p>
          <ActionButton
            className="btn-primary" busyLabel="Starting…"
            onClick={async () => { setEnrolment(await api.post<TotpEnrolment>('/api/auth/2fa/setup')); }}
          >Set up two-factor</ActionButton>
        </>
      )}

      {!status.enabled && enrolment && (
        <div style={{ display: 'flex', gap: '1.5rem', flexWrap: 'wrap', alignItems: 'flex-start' }}>
          <img
            src={enrolment.qrDataUrl}
            alt="Two-factor QR code"
            width={200}
            height={200}
            style={{ background: '#fff', padding: '0.5rem', borderRadius: '0.375rem' }}
          />
          <div style={{ flex: '1 1 18rem', minWidth: '15rem' }}>
            <p className="muted" style={{ fontSize: '0.8rem', marginTop: 0 }}>
              Scan the code, or enter this key manually:
            </p>
            <code className="mono" style={{ wordBreak: 'break-all', fontSize: '0.78rem' }}>{enrolment.secret}</code>

            <div style={{ marginTop: '0.75rem' }}>
              <label className="form-label" htmlFor="totp-confirm">Enter the 6-digit code to finish</label>
              <div style={{ display: 'flex', gap: '0.5rem' }}>
                <input
                  id="totp-confirm" className="form-input mono" placeholder="123456"
                  autoComplete="one-time-code"
                  value={code} onChange={(e) => setCode(e.target.value)}
                />
                <ActionButton
                  className="btn-primary" busyLabel="Verifying…"
                  disabled={code.trim().length < 6}
                  onClick={async () => {
                    const res = await api.post<{ backupCodes: string[] }>('/api/auth/2fa/confirm', { code });
                    setBackupCodes(res.backupCodes);
                    setEnrolment(null);
                    setCode('');
                    flash.show('Two-factor authentication enabled', 'ok');
                    await load();
                    onChanged?.();
                  }}
                >Confirm</ActionButton>
              </div>
            </div>

            <button
              type="button" className="btn-ghost btn-sm" style={{ marginTop: '0.6rem' }}
              onClick={() => { setEnrolment(null); setCode(''); }}
            >Cancel</button>
          </div>
        </div>
      )}

      {status.enabled && (
        <>
          <p className="muted" style={{ fontSize: '0.8rem' }}>
            Your password is required for both actions below. Disabling also signs out every
            device, so you will need to sign in again.
          </p>
          <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'flex-end', flexWrap: 'wrap' }}>
            <div style={{ flex: '1 1 12rem' }}>
              <label className="form-label" htmlFor="tfa-password">Password</label>
              <input
                id="tfa-password" className="form-input" type="password"
                autoComplete="current-password"
                value={password} onChange={(e) => setPassword(e.target.value)}
              />
            </div>
            <ActionButton
              busyLabel="Working…"
              disabled={!password}
              onClick={async () => {
                const res = await api.post<{ backupCodes: string[] }>('/api/auth/2fa/backup-codes', { password });
                setBackupCodes(res.backupCodes);
                setPassword('');
                flash.show('New backup codes issued — the old ones no longer work', 'ok');
                await load();
              }}
            >New backup codes</ActionButton>
            <ActionButton
              danger busyLabel="Disabling…"
              disabled={!password}
              confirm="Disable two-factor authentication? Your account will be protected by password alone, and every session will be signed out."
              onClick={async () => {
                await api.post('/api/auth/2fa/disable', { password });
                setPassword('');
                // The server dropped this session too, so send the user to sign in again.
                window.location.assign('/login');
              }}
            >Disable</ActionButton>
          </div>
        </>
      )}
    </Card>
  );
}
